import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Search, Landmark, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";

const API = getApiUrl();

/**
 * Admin -> Integrations -> Land Registry
 * Powers the agent "Estimate Price" CTA (property edit screen) with real
 * HM Land Registry sold-price comparables. Free public data, no API key —
 * just a status card, a quick postcode test lookup, and the shared
 * error-logging switch.
 */
export default function LandRegistryIntegrationSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postcode, setPostcode] = useState("NP20 4BD");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/integrations/land-registry-status`, { withCredentials: true })
      .then(({ data }) => setStatus(data))
      .catch((e) => console.error("Failed to load Land Registry status:", e))
      .finally(() => setLoading(false));
  }, []);

  const handleTest = async () => {
    if (!postcode.trim()) {
      toast.error("Enter a UK postcode to test");
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/integrations/land-registry-test-call`,
        { postcode },
        { withCredentials: true }
      );
      setResult(data);
      toast.success(`Found ${data.matches} sold transaction(s) for ${data.postcode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test lookup failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="land-registry-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="land-registry-integration-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Landmark className="h-4 w-4" />
            HM Land Registry — Price Paid Data
          </CardTitle>
          <CardDescription>
            Free public UK government sold-price data (no API key required). Powers the
            "Estimate Price" button on the agent property edit screen — real historic sold
            transactions for the property's exact postcode, combined with this site's live
            listings and AI reasoning.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm" data-testid="land-registry-status">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="font-medium">Connected</span>
            <span className="text-muted-foreground">— no setup needed, always available</span>
          </div>

          <div className="text-xs text-muted-foreground">
            <p>Used by: {status?.used_by?.join(" · ")}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="land-registry-test-postcode" className="text-sm">Test lookup by postcode</Label>
            <div className="flex items-center gap-2">
              <Input
                id="land-registry-test-postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="e.g. NP20 4BD"
                className="max-w-[200px]"
                data-testid="land-registry-test-postcode-input"
              />
              <Button onClick={handleTest} disabled={testing} size="sm" data-testid="land-registry-test-call-btn">
                {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                Test Lookup
              </Button>
            </div>
          </div>

          {result && (
            <div className="text-sm rounded-md p-3 border border-green-200 bg-green-50 text-green-800" data-testid="land-registry-test-result">
              <p className="font-medium mb-1">{result.matches} sold transaction(s) found for {result.postcode}</p>
              {result.comparables.slice(0, 5).map((c, i) => (
                <p key={i} className="text-xs font-mono">
                  {c.date} — {c.address} — £{Number(c.price || 0).toLocaleString("en-GB")}
                </p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <IntegrationApiLogToggle provider="land_registry" />
    </div>
  );
}
